import type { ISvdCard } from '@src/types/svd-product-card.types'

import wishListStateManager from '../../redux/globalStateManager/WishlistStateManager'

import svdWishlistService from './svd-wishlist.service'
import { backfillSvdWishlist, readSvdSupersetEntries, syncSvdWishlistFromCards } from './svd-wishlist.store'

/**
 * SVD login-merge — the v2 analogue of the legacy `updateWishListOnLoggedIn`. Resolves local guest
 * items → productId, pushes them to `/v2` (additive `bulk-update`), reads back the authoritative
 * list, and mirrors it into the local cache (so the heart's filled-state is right on grids too).
 *
 * Runs at TWO points, both SVD-gated:
 *  - the login event (app-level auth hook) — same trigger as the legacy merge, so a guest who
 *    wishlisted logged-out and then signs in has their items pushed to v2 immediately, regardless of
 *    which page they logged in on;
 *  - the wishlist-page fetch — which reuses the returned cards to render.
 *
 * Returns the authoritative cards so the page caller can render without a second round-trip.
 */
export async function mergeSvdWishlistOnLogin(): Promise<ISvdCard[]> {
  await backfillSvdWishlist()
  const localEntries = readSvdSupersetEntries()
  if (localEntries.length > 0) {
    await svdWishlistService.bulkUpdateWishlist(
      localEntries.map(entry => ({ productId: entry.productId, selectedAttributes: entry.selectedAttributes })),
    )
  }
  const cards = await svdWishlistService.getActiveWishlist()
  if (cards === null) {
    // GET failed (network/expired session) — not the same as a confirmed-empty wishlist. Don't let
    // it clobber already-synced local items or zero out the badge count (STRFRNT-15378 shape).
    return []
  }
  syncSvdWishlistFromCards(cards)
  wishListStateManager.setWishlistTotalItemsCount(cards.length)
  return cards
}
